import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Anchor,
  Button,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link, useParams } from 'react-router';
import { BOS_EGITIM_SUZGECI } from '@isg/validation';
import type { YetkinlikVadeDurumu } from '@isg/validation';
import {
  useAppUsers,
  useCalismaProfili,
  useCalismaProfiliYaz,
  useCompanies,
  useCompetencies,
  useContractors,
  useCreateCompetency,
  useEgitimler,
  useSaglikKanaatleri,
  useEmployments,
  useLookup,
  usePersonKimlikFull,
  usePerson,
  useIkincilOkuma,
  useUygunlukPaneliGorunur,
  useKapilar,
} from '../api/queries';
import { KIMLIK_TIPI_ETIKET } from '../i18n/tr';
import { tarihGoster } from '../lib/format';
import { Bolum, KapiSebebi, Rozet, SolukSatir } from '../sozlesme';
import { SapkaBahceDuzenleyici } from './yetki-parts';
// ⚠️ İKİSİ AYRI DOSYADIR ve öyle kalmalıdır: şapka "sistemde ne
// yapabilirsin", görevlendirme "sahada hangi görevi taşıyorsun" der (§25/1).
import { GorevlendirmeBolumu, UygunlukPaneli } from './gorevlendirme-parts';
import { KkdZimmetBolumu } from './kkd-parts';
import { IstihdamBolumu, KunyeDuzenleyici } from './calisan-parts';
import { ANLAM } from '../theme';
import type { RozetAnlami } from '../theme';
import { BosListeNotu, QueryStatus } from './parts';

// ✏️ P13-b (madde 225): ekranın beş şapka listesi (yüklenici dizini · tam kimlik ·
// yetkinlik · hesap dizini · kişi/istihdam) SUNUCUDAN okunur (`GET kapilar`) — her biri
// ilgili ucun kendi sabitidir; ekran şapka dizgesi yazmaz, yazamayan sebebi okur.

/**
 * VADE ROZETİ (F4·S5·B4 — P3): durum SUNUCUDAN gelir (`vadeDurumu` —
 * eşik `resolveUyariEsigi`den, hesap `yetkinlikVadeDurumu`da; ekran 30
 * yazmaz, tarih kıyaslamaz). Burada yalnız durum → ANLAM eşlenir; renk
 * Rozet bileşeninin işidir (eşleme theme.ts'te, tek yerde).
 */
const VADE_ROZETI: Record<YetkinlikVadeDurumu, { anlam: RozetAnlami; etiket: string }> = {
  gecerli: { anlam: 'olumlu', etiket: 'Geçerli' },
  yaklasiyor: { anlam: 'dikkat', etiket: 'Yaklaşıyor' },
  doldu: { anlam: 'gecikmis', etiket: 'Süresi doldu' },
  // Süresiz bir DURUM değil ETİKETTİR: vade eksenine hiç girmez (§19/11-7).
  suresiz: { anlam: 'bilgi', etiket: 'Süresiz' },
  // K9 (tur 5): başlangıcı gelecekte — bugün taşınmaz (tek yüklem, sunucuda).
  henuz_degil: { anlam: 'gecikmis', etiket: 'Henüz geçerli değil' },
};

/**
 * Kişi kartı (F1·S10; madde 37 kapanışı 05.08.2026; F4·S5·B4 demo turu):
 *
 * - **SAYFA BAŞLIĞI KAYDIN KENDİSİDİR** (P5 uyumu): başlık kişinin adı,
 *   altında bağlam (işveren · görev · istihdam durumu) ve kimlik satırı.
 *   Ayrı "Kimlik" kartı KALKTI — başlık bloğu aynı bilgiyi taşır (testid
 *   `kisi-kimlik-karti` blokta yaşamaya devam eder).
 * - Kimlik MASKELİ gelir; "Tam göster" yalnız yetkili şapkalarda görünür ve
 *   HER gösterim sunucuda loglanır. DOĞUM TARİHİ satırı YOK (S10 kararı).
 * - Kişi ≠ istihdam (§2.7): yetkinlik KİŞİNİN niteliğidir, işveren değişse
 *   de kişiyle taşınır.
 */
export function PersonCardScreen(): React.JSX.Element {
  const { personId } = useParams<{ personId: string }>();
  // ✏️ **TUR 9 / B4 (T9-03): KART ARTIK TEKİL UÇTAN OKUR.** Eskiden tüm
  // listeyi ('hepsi') indirip içinden kendi kaydını ayıklıyordu — 400
  // çalışanlı bir tenant'ta bir kartı açmak 400 satır demekti. Bahçe kapısı
  // uçta ve liste ucuyla AYNI yüklemdir; bahçe dışı kişide 403 gelir ve
  // aşağıdaki "bulunamadı" bloğu onu da karşılar.
  const persons = usePerson(personId ?? '');
  const person = persons.data;

  if ((persons.isSuccess && !person) || persons.isError) {
    return (
      <Stack>
        <Title order={3}>Kişi kartı</Title>
        <BosListeNotu mesaj="Kişi bulunamadı ya da bahçenizin dışında." />
        <Anchor component={Link} to="/calisanlar" size="sm">
          ← Çalışan listesine dön
        </Anchor>
      </Stack>
    );
  }

  return (
    <Stack>
      <Anchor component={Link} to="/calisanlar" size="sm" data-testid="detay-geri">
        ← Çalışanlar
      </Anchor>
      <QueryStatus queries={[persons]} kayit />
      {person && personId && <PersonCardBody personId={personId} person={person} />}
    </Stack>
  );
}

function PersonCardBody({
  personId,
  person,
}: {
  personId: string;
  person: NonNullable<ReturnType<typeof usePerson>['data']>;
}): React.JSX.Element {
  const employments = useEmployments(personId);
  const companies = useCompanies();
  const kapilar = useKapilar();
  // Yüklenici adları yalnız o listeyi okuyabilen şapkalarda çekilir —
  // uzman gibi şapkalarda sorgu hiç atılmaz (403 gürültüsü üretmemek için;
  // işveren o durumda "Yüklenici firması" olarak etiketlenir). Liste sunucudan.
  const yukleniciOkur = kapilar.data?.yukleniciDiziniKapisi.yazabilir ?? false;
  const contractors = useContractors({ enabled: yukleniciOkur });
  const contractorsData = contractors.data;

  // Tam gösterim (madde 37): buton yalnız yetkili şapkada; istek ancak
  // tıklanınca atılır — her istek sunucuda gösterim log'u yazar.
  const kimlikFullOkur = kapilar.data?.kimlikGosterimKapisi.yazabilir ?? false;
  // K11 + B1: künye düzeltmesi ve istihdam yazma AYNI kapıdır
  // (kişiyi açabilen adını düzeltir, istihdamını açar/kapatır — §20.5).
  const kisiYazar = kapilar.data?.kisiKapisi.yazabilir ?? false;
  const istihdamYazar = kisiYazar;
  const [kunyeAcik, setKunyeAcik] = useState(false);
  const [tamGosterildi, setTamGosterildi] = useState(false);
  const kimlikFull = usePersonKimlikFull(personId, { enabled: tamGosterildi });
  // MADDE 291 — kişi kartını Çalışanlar listesini gören her iç şapka açar;
  // görevlendirme bölümü yalnız görevlendirme okuma listesine çizilir. Bayrak
  // SUNUCUDAN (`ikincilOkuma.gorevlendirme` — iki ucun `@RequireRole`
  // sabiti); kapalıysa bölüm çizilmez, uç çağrılmaz.
  const gorevlendirmeOkuma = useIkincilOkuma('gorevlendirme');
  // MADDE 295 — uygunluk paneli AYRI listedir: alan sorumlusu · hekim · YSP
  // bahçesindeki kişide görür; çalışan yalnız KENDİ kartında (kimlikten).
  const uygunlukPaneliGorunur = useUygunlukPaneliGorunur(personId);

  const isverenAdi = (e: { companyId: string | null; contractorId: string | null }): string =>
    e.companyId
      ? (companies.data?.find((c) => c.id === e.companyId)?.ad ?? 'Şirket')
      : (contractorsData?.find((c) => c.id === e.contractorId)?.ad ?? 'Yüklenici firması');

  // Bağlam satırı: AKTİF istihdam (bitiş boş) önceliklidir; yoksa son kayıt.
  const aktifIstihdam = employments.data?.find((e) => e.bitis === null) ?? null;

  return (
    <Stack>
      {/* Başlık bloğu = eski Kimlik kartı (testid korunur — e2e/menü akışları
          bu bloğun görünürlüğünü kişi kartının kanıtı sayar). */}
      <Stack gap={4} data-testid="kisi-kimlik-karti">
        <Title order={3} data-testid="kisi-ad">
          {person.adSoyad}
        </Title>
        <Group gap="xs" data-testid="kisi-baglam">
          {employments.isSuccess && aktifIstihdam && (
            <Text size="sm" c="dimmed">
              {aktifIstihdam.rol} · {isverenAdi(aktifIstihdam)}
            </Text>
          )}
          {employments.isSuccess &&
            (aktifIstihdam ? (
              <Rozet anlam="olumlu" testId="kisi-istihdam-rozeti">
                Aktif istihdam
              </Rozet>
            ) : (
              <Rozet anlam="bilgi" testId="kisi-istihdam-rozeti">
                Aktif istihdamı yok
              </Rozet>
            ))}
        </Group>
        <Text size="sm" data-testid="kisi-kimlik">
          {KIMLIK_TIPI_ETIKET[person.kimlikTipi]} ·{' '}
          {kimlikFull.data ? kimlikFull.data.kimlikNo : person.kimlikNoMaskeli}
          {kimlikFullOkur && !kimlikFull.data && (
            <Button
              size="compact-xs"
              variant="subtle"
              ml="xs"
              data-testid="kimlik-tam-goster"
              loading={tamGosterildi && kimlikFull.isFetching}
              onClick={() => setTamGosterildi(true)}
            >
              Tam göster
            </Button>
          )}
          {kimlikFull.isError && (
            <Text size="xs" c={ANLAM.engel} component="span" ml="xs" role="alert">
              Tam kimlik okunamadı
            </Text>
          )}
        </Text>
        {/* P13-b — tam kimlik kapısı; <p> içine <p> girmesin diye kimlik satırının DIŞINDA. */}
        {kapilar.data && !kimlikFull.data && (
          <KapiSebebi
            durum={kapilar.data.kimlikGosterimKapisi}
            nesne="Tam kimliği"
            fiil="görür"
            testId="kimlik-yazamama-sebebi"
          />
        )}
        <Text size="xs" c="dimmed">
          Sicil no: {person.personelSicilNo ?? '—'} · E-posta: {person.email ?? '—'} · Kimlik no
          maskeli gösterilir; tam gösterim yetkiye tabidir ve her gösterim kayıt altına alınır.
        </Text>
        {/* ✏️ TUR 9 / K11 — KÜNYE DÜZELTMESİ. Kimlik no/tipi HARİÇ. */}
        {kisiYazar && !kunyeAcik && (
          <Button
            size="compact-xs"
            variant="subtle"
            w={140}
            data-testid="kunye-duzenle-ac"
            onClick={() => setKunyeAcik(true)}
          >
            Künyeyi düzelt
          </Button>
        )}
        {kisiYazar && kunyeAcik && (
          <KunyeDuzenleyici
            personId={personId}
            adSoyad={person.adSoyad}
            personelSicilNo={person.personelSicilNo}
            email={person.email}
            onKapat={() => setKunyeAcik(false)}
          />
        )}
      </Stack>

      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* ✏️ **TUR 9 / B1 · B2 · B5:** istihdam bölümü artık YAZILABİLİR —
            aç · kapat (ayrılış) · **nakil** (tek adım, iki uç) · rol/başlangıç
            düzeltmesi. Yazma yolu `calisan-parts.tsx`te TEKTİR. */}
        <Bolum baslik="İstihdamlar" testId="kisi-istihdamlar">
          <QueryStatus queries={[employments]} />
          {employments.data && (
            <IstihdamBolumu
              personId={personId}
              employments={employments.data}
              yazabilir={istihdamYazar}
              isverenAdi={isverenAdi}
            />
          )}
          {employments.data?.length === 0 && !istihdamYazar && (
            <BosListeNotu mesaj="Kayıtlı istihdam yok." />
          )}
          {/* ✏️ Gezinti artçısı 2 / B3 envanteri — salt okunur + sebep (P13-b: kapıdan). */}
          {employments.data && kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.kisiKapisi}
              nesne="İstihdamı"
              fiil="açar"
              testId="istihdam-yazamama-sebebi"
            />
          )}
          {employments.data && kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.yukleniciDiziniKapisi}
              nesne="Yüklenici firma adlarını"
              fiil="görür"
              testId="yuklenici-dizini-yazamama-sebebi"
            />
          )}
        </Bolum>

        <YetkinlikBolumu personId={personId} />
        <EgitimGecmisi personId={personId} />
        <SaglikBolumu personId={personId} />
        <CalismaProfiliBolumu personId={personId} />
        <SapkaBahceBolumu personId={personId} />
      </SimpleGrid>

      {/* ⚠️ **TUR 4½ / B1 — GÖREVLENDİRMELER IZGARANIN DIŞINDA, TAM
          GENİŞLİKTE** (P9'un ruhu: kısa alanlar ızgarada, GENİŞ içerik tam
          genişlikte). İlk yazımda ızgaraya kondu ve **e2e bunu yakaladı:**
          bölüm ALTI kolon taşır (görev · ekipman tipi · işyeri · başlangıç ·
          durum + eylem) ve yarım genişlikte *"Görev"* kolonu ~40px'e
          sıkışıyordu — açılır liste, kalem adları OKUNAMAYACAK kadar dar bir
          şeride dönüşüyor ve tıklanamıyordu.
          ⚠️ **§2.40'ın az konuşulan kolu: kontrol GÖRÜNÜYOR ve ÇALIŞIYOR ama
          KULLANILAMIYOR.** Ne sessizce düşüyor ne hata veriyor — yalnızca
          sığmıyor; kullanıcı bunu bir arıza olarak bile bildiremez.
          ⚠️ Şapkanın YANINDA durma kararı (iki ayrı kavram, iki ayrı bölüm)
          DEĞİŞMEDİ — değişen yalnız hangi GENİŞLİKTE durduğu. */}
      {gorevlendirmeOkuma.acik && <GorevlendirmeBolumu personId={personId} />}

      {/* KKD ZİMMETİ (Kapsam §30/Z7 — madde 170 kapandı). Görevlendirmenin
          KARDEŞİ: ızgaranın dışında, tam genişlikte (altı kolon + soluk satır).
          ⚠️ Uygunluk paneline GİRMEZ (Z4 — vadesiz kayıt bir şart değildir);
          veri kaynağı artık VAR, bölüm her kişide çizilir — kaydı olmayanda
          "kayıt yok" satırı. */}
      <KkdZimmetBolumu personId={personId} />

      {/* Tur 4½ / B2 — UYGUNLUK PANELİ (G6). Izgaranın DIŞINDA ve TAM
          GENİŞLİKTE: panel yukarıdaki bölümlerin ÖZETİ değil, onların
          birlikte cevapladığı soru — "bu kişi taşıması gereken belgeleri
          taşıyor mu?" */}
      {uygunlukPaneliGorunur && <UygunlukPaneli personId={personId} />}
    </Stack>
  );
}

/**
 * YETKİNLİKLER (F4·S5·B4). Rozet durumu SUNUCUDAN (`vadeDurumu`); yetkinlik
 * ALT KALEMDİR (kendi sayfası yok — P1) ve SOLUK SATIRLA satır içi girilir
 * (P1-a): boş listede tek başına durur, kaydedilen satır üstüne oturur.
 */
function YetkinlikBolumu({ personId }: { personId: string }): React.JSX.Element {
  const competencies = useCompetencies(personId);
  // P13-b (madde 225): yetkinlik kapısı SUNUCUDAN (`YETKINLIK_YAZMA` — uçla aynı sabit).
  const kapilar = useKapilar();
  const yazabilir = kapilar.data?.yetkinlikKapisi.yazabilir ?? false;
  // MADDE 291 — tip sözlüğü yalnız EKLEME SATIRININ seçicisini besler (satırlar
  // adı sunucudan taşır — `yetkinlikTipiAd`). Yazamayan şapkada satır çizilmez,
  // sözlük de ÇAĞRILMAZ: lokasyon yöneticisi · teknisyen · alan sorumlusu ·
  // hekim · YSP kartı her açtığında `lookup/yetkinlik_tipi` 403 dönüyordu.
  const yetkinlikTipleri = useLookup('yetkinlik_tipi', { enabled: yazabilir });
  const olustur = useCreateCompetency(personId);

  const [tipId, setTipId] = useState<string | null>(null);
  const [baslangic, setBaslangic] = useState('');
  const [bitis, setBitis] = useState('');
  const dokunuldu = tipId !== null || baslangic !== '' || bitis !== '';
  // P12 (madde 224) — kaydedilmemiş yetkinlik satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'yetkinlik satırı');
  const kaydedilebilir = tipId !== null && baslangic !== '' && bitis !== '' && bitis >= baslangic;
  const kaydet = (): void => {
    if (!kaydedilebilir || tipId === null) return;
    olustur.mutate(
      { yetkinlikTipiId: tipId, gecerlilikBaslangic: baslangic, gecerlilikBitis: bitis },
      {
        onSuccess: () => {
          setTipId(null);
          setBaslangic('');
          setBitis('');
        },
      },
    );
  };

  return (
    <Bolum baslik="Yetkinlikler" testId="kisi-yetkinlikler">
      <QueryStatus queries={[competencies]} />
      {competencies.data && (
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Yetkinlik</Table.Th>
              <Table.Th>Geçerlilik</Table.Th>
              <Table.Th>Durum</Table.Th>
              {yazabilir && <Table.Th w={44} aria-label="Satır eylemi" />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {competencies.data.map((c) => {
              const rozet = VADE_ROZETI[c.vadeDurumu];
              return (
                <Table.Tr key={c.id} data-testid="yetkinlik-satiri">
                  <Table.Td>{c.yetkinlikTipiAd}</Table.Td>
                  <Table.Td>
                    {tarihGoster(c.gecerlilikBaslangic)} –{' '}
                    {c.gecerlilikBitis === null ? 'süresiz' : tarihGoster(c.gecerlilikBitis)}
                  </Table.Td>
                  <Table.Td>
                    <Rozet anlam={rozet.anlam} testId="yetkinlik-rozeti">
                      {rozet.etiket}
                    </Rozet>
                  </Table.Td>
                  {yazabilir && <Table.Td />}
                </Table.Tr>
              );
            })}
            {yazabilir && (
              <SolukSatir
                testId="yetkinlik-soluk-satir"
                dokunuldu={dokunuldu}
                kaydedilebilir={kaydedilebilir}
                kaydediliyor={olustur.isPending}
                onKaydet={kaydet}
                kolonSayisi={3}
              >
                <Table.Td>
                  <Select
                    searchable
                    placeholder="Yetkinlik seç"
                    aria-label="Yetkinlik tipi"
                    data-testid="yetkinlik-soluk-tip"
                    value={tipId}
                    onChange={setTipId}
                    data={(yetkinlikTipleri.data ?? [])
                      .filter((t) => !t.gizli)
                      .map((t) => ({ value: t.id, label: t.ad }))}
                  />
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <TextInput
                      type="date"
                      aria-label="Geçerlilik başlangıcı"
                      data-testid="yetkinlik-soluk-baslangic"
                      value={baslangic}
                      onChange={(e) => setBaslangic(e.currentTarget.value)}
                    />
                    <TextInput
                      type="date"
                      aria-label="Geçerlilik bitişi"
                      data-testid="yetkinlik-soluk-bitis"
                      value={bitis}
                      onChange={(e) => setBitis(e.currentTarget.value)}
                    />
                  </Group>
                </Table.Td>
                <Table.Td />
              </SolukSatir>
            )}
          </Table.Tbody>
        </Table>
      )}
      {!yazabilir && competencies.data?.length === 0 && (
        <BosListeNotu mesaj="Kayıtlı yetkinlik yok." />
      )}
      {/* ✏️ Gezinti artçısı 2 / B3 envanteri — salt okunur + sebep (P13-b: kapıdan). */}
      {competencies.data && kapilar.data && (
        <KapiSebebi
          durum={kapilar.data.yetkinlikKapisi}
          nesne="Yetkinliği"
          fiil="ekler"
          testId="yetkinlik-yazamama-sebebi"
        />
      )}
      {olustur.isError && (
        <Text size="xs" c={ANLAM.engel} role="alert" mt="xs">
          Yetkinlik kaydedilemedi: {olustur.error.message}
        </Text>
      )}
    </Bolum>
  );
}

/**
 * ŞAPKA + BAHÇE (F4·B2 — karar **Y1**, Kapsam §20.7).
 *
 * > **Kişi kartı ASIL yüzeydir: atama burada yaşar.** *"Ahmet ne yapabiliyor,
 * > nerede?"* sorusunun cevabı kişinin kendi kartındadır.
 *
 * ⚠️ **Kullanıcılar ekranı AYNI uçları çağırır** (`yetki-parts.tsx` — tek
 * bileşen, tek yazma yolu). Bu bölüm bir kopya DEĞİL, aynı bileşenin bir
 * çağrısıdır.
 *
 * ⚠️ **HESABI OLMAYAN KİŞİYE ŞAPKA VERİLEMEZ** ve bu bir eksik değil,
 * Y3'ün sınırıdır: şapka **hesaba** asılır (`role_assignment.app_user_id`).
 * Y3'ten sonra e-postası olan kişi kaydıyla birlikte **pasif hesap doğar**;
 * e-postası olmayanda hesap açılmaz (eşleşme anahtarı bugün e-postadır —
 * madde 133 AÇIK) ve ekran bunu **söyler**, sessizce boş durmaz.
 */
function SapkaBahceBolumu({ personId }: { personId: string }): React.JSX.Element | null {
  // P13-b (madde 225): hesap dizini kapısı SUNUCUDAN (`KULLANICI_OKUMA` — `GET app-users`
  // ile aynı sabit). Okuyamayan şapkada bölüm BOŞ DÖNMEZ, sebebini söyler (§2.40).
  const kapilar = useKapilar();
  const gorebilir = kapilar.data?.hesapDiziniKapisi.yazabilir ?? false;
  const kullanicilar = useAppUsers({ personId, enabled: gorebilir });
  if (!kapilar.data) return null;
  if (!gorebilir) {
    return (
      <Bolum baslik="Şapka ve bahçe" testId="kisi-yetki">
        <KapiSebebi
          durum={kapilar.data.hesapDiziniKapisi}
          nesne="Şapka ve bahçeyi"
          fiil="görür"
          testId="kisi-yetki-yazamama-sebebi"
        />
      </Bolum>
    );
  }
  const kullanici = kullanicilar.data?.[0] ?? null;
  return (
    <Bolum baslik="Şapka ve bahçe" testId="kisi-yetki">
      <QueryStatus queries={[kullanicilar]} />
      {kullanicilar.isSuccess && !kullanici && (
        // ✏️ Yetki artçısı 2 (30.09.2026): cümle ÖLÇÜLEN davranışı söyler. Önceki
        // hâli *"Kişi kaydında e-posta varsa hesap otomatik açılır"* diyordu;
        // e-posta SONRADAN yazıldığında hesap doğmaz (pasif hesap yalnız kişi
        // kaydı açılırken doğar). ⚠️ Hesap mekanizmasına dokunulmadı.
        <BosListeNotu mesaj="Bu kişinin hesabı yok — şapka hesaba verilir. Hesap yalnız kişi kaydı e-postayla oluşturulurken kendiliğinden açılır; e-posta sonradan yazıldıysa kişi ilk kez giriş yaptığında açılır." />
      )}
      {kullanici && (
        <>
          {kullanici.hicGirmedi && (
            <Group gap="xs" mb="xs">
              <Rozet anlam="bilgi" testId="kisi-yetki-pasif">
                Hesap hazır — kişi henüz giriş yapmadı
              </Rozet>
              <Text size="xs" c="dimmed">
                Şapka ve bahçe şimdi verilebilir; kişi ilk girişinde hazır bulur.
              </Text>
            </Group>
          )}
          {/* P13 — liste `POST/DELETE role-assignments`ın @RequireRole'ünün TA KENDİSİ
              (`SAPKA_VERME_SAPKALARI`); ekranda ikinci şapka dizgesi yazılmaz. */}
          <SapkaBahceDuzenleyici
            kullanici={kullanici}
            yazabilir={kapilar.data.sapkaVermeKapisi.yazabilir}
            testIdOnek="kisi-yetki"
          />
          <KapiSebebi
            durum={kapilar.data.sapkaVermeKapisi}
            nesne="Şapkayı"
            fiil="verir"
            testId="kisi-yetki-yazamama-sebebi"
          />
        </>
      )}
    </Bolum>
  );
}

/**
 * EĞİTİM GEÇMİŞİ (F3B·S7 — T1/2). Kayıt KİŞİYE bağlıdır: kişi şirket
 * değiştirse de geçmişi onunla gelir. **Eski istihdamdan gelen kayıt
 * GÖRÜNÜR ama uygunlukta SAYILMAZ** (temel İSG işe girişte tekrar ister —
 * §19/11-6); şerh ekranda yazılır, süzgeç sunucudadır (bahçe filtresi).
 *
 * Eğitim BAĞIMSIZ KAYITTIR (kendi sayfası var — P1): buradan satır içi
 * eklenmez; kaydı eğitim modülü açar. Satırdaki rozet, eğitimden DOĞAN
 * yetkinliğin vadesidir (`vadeDurumu` — sunucudan).
 */
function EgitimGecmisi({ personId }: { personId: string }): React.JSX.Element {
  // Y1 (tur 5): liste sayfalanmış uçtan gelir; kişi kartı kişi süzgeciyle
  // okur (kişinin eğitim geçmişi tek sayfaya sığar — sayfa boyutu üst sınır).
  const egitimlerSorgusu = useEgitimler({ ...BOS_EGITIM_SUZGECI, personId });
  const egitimler = { ...egitimlerSorgusu, data: egitimlerSorgusu.data?.satirlar };
  return (
    <Bolum baslik="Eğitim geçmişi" testId="kisi-egitimler">
      <QueryStatus queries={[egitimlerSorgusu]} />
      {egitimler.data?.length === 0 && <BosListeNotu mesaj="Kayıtlı eğitim yok." />}
      {egitimler.data && egitimler.data.length > 0 && (
        <>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tarih</Table.Th>
                <Table.Th>Eğitim</Table.Th>
                <Table.Th>İşyeri</Table.Th>
                <Table.Th>Süre</Table.Th>
                <Table.Th>Belge vadesi</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {egitimler.data.map((kayit) => {
                const rozet = kayit.vadeDurumu ? VADE_ROZETI[kayit.vadeDurumu] : null;
                return (
                  <Table.Tr key={kayit.id} data-testid="kisi-egitim-satiri">
                    <Table.Td>{tarihGoster(kayit.tarih)}</Table.Td>
                    <Table.Td>{kayit.egitimTuruAd}</Table.Td>
                    <Table.Td>{kayit.workplaceAd}</Table.Td>
                    <Table.Td>
                      {kayit.sureDersSaati === null ? '—' : `${kayit.sureDersSaati} ders saati`}
                    </Table.Td>
                    <Table.Td>
                      {rozet ? (
                        <Group gap={6} wrap="nowrap">
                          {kayit.vadeBitis && (
                            <Text size="sm" component="span">
                              {tarihGoster(kayit.vadeBitis)}
                            </Text>
                          )}
                          <Rozet anlam={rozet.anlam} testId="kisi-egitim-rozeti">
                            {rozet.etiket}
                          </Rozet>
                        </Group>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <Text size="xs" c="dimmed" mt="xs">
            Eski istihdamdan gelen kayıtlar da burada görünür; uygunluk kontrolünde sayılmayabilir
            (temel İSG yeni işe girişte tekrar ister).
          </Text>
        </>
      )}
    </Bolum>
  );
}

/**
 * SAĞLIK BÖLÜMÜ (F3B·S8 — Kapsam §16.11/10).
 *
 * **YALNIZ YETKİLİ ŞAPKALARA GÖRÜNÜR:** izleyici · saha sorumlusu · çalışan ·
 * yüklenici temsilcisi göremez. Karar SUNUCUDADIR (uç 403); ekran bölümü
 * `ikincilOkuma.saglikKanaati` bayrağından çizer ve sorgu kancası aynı bayrağı
 * KENDİ içinde okur — dar okuma listesinin tek kaynağı `SAGLIK_OKUMA_SAPKALARI`dır.
 *
 * ✏️ MADDE 291 (yetki artçısı 2, 30.09.2026): önceki hâlde ekran yetkisiz
 * şapkada kancaya `null` veriyor, kanca kişi süzgeci olmadan sağlık ucunu
 * çağırıyordu (403). **Sağlık ucu, sağlık yetkisi olmayan şapkada ASLA
 * çağrılmaz** — koruma artık kancanın içindedir.
 *
 * **GEREKÇE YOKTUR** (S1): kısıt ve vade görünür; tanı/tetkik/bulgu YOK.
 * Bu kapı bilerek kapalı — buraya tanı/bulgu/tetkik alanı EKLENMEZ.
 */
function SaglikBolumu({ personId }: { personId: string }): React.JSX.Element | null {
  const gorebilir = useIkincilOkuma('saglikKanaati').acik;
  const kanaatler = useSaglikKanaatleri(personId);
  if (!gorebilir) return null;
  const guncel = kanaatler.data?.[0] ?? null;
  return (
    // S2 (tur 5): iki kutu ayrı kalır, başlıkları ayrımı söyler — burası
    // KANAATİN KENDİSİ (hekim kararı + kısıtlar); uygunluk panelindeki
    // "Sağlık (uygunluk şartı)" ise şartın vade/varlık okuması.
    <Bolum baslik="Hekim kanaati ve kısıtlar" testId="kisi-saglik">
      <QueryStatus queries={[kanaatler]} />
      {kanaatler.isSuccess && !guncel && <BosListeNotu mesaj="Kayıtlı sağlık kanaati yok." />}
      {guncel && (
        <Group gap="xs" data-testid="kisi-saglik-ozet">
          <Rozet anlam={guncel.vadesiDoldu ? 'gecikmis' : 'olumlu'} testId="kisi-saglik-karar">
            {guncel.uygunlukKarari}
          </Rozet>
          <Text size="sm">Geçerlilik: {tarihGoster(guncel.gecerlilikBitis)}</Text>
          {guncel.vadesiDoldu && (
            <Rozet anlam="gecikmis" testId="kisi-saglik-vade">
              Vadesi doldu
            </Rozet>
          )}
          {/* B7 — işaretli kalemler hekimin cümlesiyle (olumlu/olumsuz). */}
          {guncel.kisitlar.map((kisit) => (
            <Rozet key={kisit.id} anlam={kisit.isaret === 'uygun' ? 'olumlu' : 'dikkat'}>
              {kisit.ifade}
            </Rozet>
          ))}
        </Group>
      )}
      <Text size="xs" c="dimmed" mt="xs">
        Sistemde yalnız kanaat tutulur — tanı, bulgu ve tetkik sonuçları kaydedilmez.
      </Text>
    </Bolum>
  );
}

/**
 * İBYS ÇALIŞMA PROFİLİ (tur 7 / U2; §16.11/9-C).
 *
 * ⚠️ **UÇ VARDI, YOL YOKTU:** `GET/PUT person-work-profile/:personId`
 * F3B·S8'den beri duruyor ve **hiçbir ekran onu çağırmıyordu** — sonuç:
 * İBYS dışa aktarmasının **son 11 sütunu daima BOŞ** çıkıyordu. Bu bölüm o
 * boşluğu kapatır.
 *
 * ⚠️ **ALANLAR SERBEST METİNDİR ve öyle kalır (J/C):** ISCO ve Bakanlık
 * sınıf listeleri binlerce kalemdir ve entegratörlük turunda (Faz 5) iner;
 * buraya açılır liste bağlamak **değer uydurmak** olurdu (Yönerge §5). Boş
 * alan dışa aktarmada **boş çıkar** — hekim elle tamamlar.
 *
 * ⚠️ **PROFİL MUAYENENİN DEĞİL, KİŞİNİN/GÖREVİN özelliğidir:** bir kez
 * girilir, her muayenede tekrar sorulmaz. Kapı sağlık kanaatinin kapısıyla
 * AYNI (dar okuma listesi + `saglik_kanaat_giris` yazma listesi) — ikinci
 * bir yetki ekseni açılmaz.
 */
function CalismaProfiliBolumu({ personId }: { personId: string }): React.JSX.Element | null {
  const gorebilir = useIkincilOkuma('saglikKanaati').acik;
  const profil = useCalismaProfili(personId);
  const yaz = useCalismaProfiliYaz(personId);
  const [taslak, setTaslak] = useState<Record<string, string> | null>(null);
  if (!gorebilir) return null;
  // ✏️ P13 · B3 — form OKUMA listesindeki her şapkaya çiziliyordu, yazamayan
  // kaydedince 403 alıyordu. Yazma kapısı `PUT`un adım listesinden
  // (`saglik_kanaat_giris`); yazamayan salt okunur görür + sebep.
  const yazmaKapisi = profil.data?.yetkiler.yazmaKapisi;
  const yazabilir = yazmaKapisi?.yazabilir ?? false;

  const mevcut = profil.data;
  const deger = (alan: string): string =>
    taslak?.[alan] ?? (mevcut?.[alan as keyof typeof mevcut] as string | null) ?? '';
  const duzenle = (alan: string, v: string): void =>
    setTaslak((onceki) => ({ ...(onceki ?? {}), [alan]: v }));

  return (
    <Bolum
      baslik="Çalışma profili (İBYS)"
      testId="kisi-calisma-profili"
      aciklama="İBYS muayene bildiriminin ortam ve meslek alanları. Boş bırakılan alan dışa aktarmada boş çıkar."
    >
      <QueryStatus queries={[profil]} />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs">
        {CALISMA_PROFILI_ALANLARI.map(({ alan, etiket }) => (
          <TextInput
            key={alan}
            size="xs"
            label={etiket}
            value={deger(alan)}
            readOnly={!yazabilir}
            data-testid={`calisma-profili-${alan}`}
            onChange={(e) => duzenle(alan, e.currentTarget.value)}
          />
        ))}
      </SimpleGrid>
      {yazmaKapisi && (
        <KapiSebebi
          durum={yazmaKapisi}
          nesne="Çalışma profilini"
          fiil="girer"
          testId="calisma-profili-yazamama-sebebi"
        />
      )}
      {yazabilir && (
        <Group mt="xs">
          <Button
            size="xs"
            disabled={taslak === null || yaz.isPending}
            data-testid="calisma-profili-kaydet"
            onClick={() => {
              const govde: Record<string, string | null | string[]> = { equipmentIds: [] };
              for (const { alan } of CALISMA_PROFILI_ALANLARI) {
                const v = deger(alan).trim();
                govde[alan] = v.length > 0 ? v : null;
              }
              yaz.mutate(govde as never, { onSuccess: () => setTaslak(null) });
            }}
          >
            Profili kaydet
          </Button>
          {taslak !== null && (
            <Text size="xs" c="dimmed">
              Kaydedilmemiş değişiklik var.
            </Text>
          )}
          {yaz.isError && (
            <Text size="xs" c={ANLAM.engel} role="alert" data-testid="calisma-profili-hata">
              {yaz.error.message}
            </Text>
          )}
        </Group>
      )}
    </Bolum>
  );
}

/** İBYS payload'ının alan düzeniyle AYNI sıra (sıra bir sözleşmedir). */
const CALISMA_PROFILI_ALANLARI = [
  { alan: 'meslekKodu', etiket: 'Meslek kodu' },
  { alan: 'maruziyetFizikiOrtam', etiket: 'Fiziki ortam' },
  { alan: 'maruziyetHava', etiket: 'Hava' },
  { alan: 'maruziyetGurultu', etiket: 'Gürültü' },
  { alan: 'maruziyetElektrik', etiket: 'Elektrik' },
  { alan: 'maruziyetToz', etiket: 'Toz' },
  { alan: 'maruziyetKimyasal', etiket: 'Kimyasal' },
  { alan: 'maruziyetBiyolojik', etiket: 'Biyolojik' },
  { alan: 'calismaPozisyonu', etiket: 'Çalışma pozisyonu' },
  { alan: 'calismaZamani', etiket: 'Çalışma zamanı' },
  { alan: 'aracKullanimi', etiket: 'Araç kullanımı' },
] as const;
